import type { JobCategoryForSettings } from '@utils/businessCategoryUtils';
import type {
	AdditionalCustomers,
	Address,
	AddressTemplates,
	Branch,
	Business,
	Contact,
	Customer,
	CustomStage,
	Document,
	Estimate,
	EstimateGroup,
	EstimateLineItem,
	Installation,
	Job,
	JobCustomStage,
	JobLog,
	KeyDate,
	Measurement,
	PDFType,
	PurchaseOrder,
	QuickbooksClass,
	QuickbooksLog,
	Quote,
	QuoteSettings,
	ScheduledJob,
	Task,
	Transaction,
	User,
	UsersOnJobs,
	UsersOnScheduledJobs,
} from '@prisma/client';
import {
	AIABillingTerms,
	BusinessCategory,
	EstimateState,
	JobCategories,
	JobLogType,
	JobPhase,
	LossReasonCodes,
	Prisma,
	PurchaseOrderStatus,
	ReferralSource,
	ScheduledJobType,
} from '@prisma/client';
import { hiddenInputNumber } from '@utils/reactHookForm';
import { z } from 'zod';
import { DashboardInputSchema } from './analytics/dashboard.schema';
import type { CustomerFull } from './customer.schema';
import type { ScheduledJobForModalCard } from './scheduledJob.schema';

export type DuplicateJobOptions = z.infer<typeof JobSchema.duplicateJobOptionsSchema>;
export type EditJobSchema = z.infer<typeof JobSchema.editSchema>;

export type FormattedJobForProfitAndLoss = {
	deadline: string;
	address: string;
	category: string;
	updatedAt: string;
	isPickup: boolean;
	id: number;
	business?: {
		addressId: number;
	};
	businessId: number;
	customerId: number;
	customerInstallationContactId?: number;
	jobOwnerId: number;
	addressId: number;
	jobPhase: JobPhase;
	businessCategory: BusinessCategory;
	jobCategoryId: number | null;
	notes?: string;
	workOrderNotes?: string;
	name: string;
	lead?: string;
	description?: string;
	lossReason?: string;
	billingDayOfMonth?: number;
	retainage?: number;
	lossReasonCode?: LossReasonCodes;
	createdAt: Date;
	deletedAt?: Date;
	customer: Customer & {
		email?: string | null;
		phone?: string | null;
	};
	jobOwner: User;
	estimates: Estimate[];
	isAIAProgressBilling?: boolean;
	contactName?: string;
	lastStatus?: JobLogType;
	lastStatusTime?: Date;
	estimatedTotal?: number;
	estimateBalance?: number;
	materialsTotal?: number;
	laborTotal?: number;
	otherTotal?: number;
	purchaseOrderTotal?: number;
	techniciansOnJob?: { firstName: string; lastName: string }[];
	purchaseOrders?: { status: PurchaseOrderStatus; businessSupplier: { supplier: { name: string } } }[];
	problemReason: string | null;
	flaggedOn?: Date;
};

export type FormattedJob = {
	deadline: string;
	address: string;
	category: string;
	updatedAt: string;
	isPickup: boolean;
	id: number;
	internalJobId?: string | null;
	business?: {
		addressId: number;
	};
	branch?: Branch;
	businessId: number;
	customerId: number;
	customerInstallationContactId?: number;
	jobOwnerId: number;
	addressId: number;
	jobPhase: JobPhase;
	businessCategory: BusinessCategory;
	notes?: string;
	workOrderNotes?: string;
	name: string;
	lead?: string;
	description?: string;
	lossReason?: string;
	billingDayOfMonth?: number;
	retainage?: number;
	lossReasonCode?: LossReasonCodes;
	createdAt: Date;
	bidDueDate?: Date;
	deletedAt?: Date;
	promiseDate?: Date;
	customer: Customer & {
		email?: string | null;
		phone?: string | null;
		billingAddress?: string;
	};
	jobOwner: User;
	estimates: Estimate[];
	isAIAProgressBilling?: boolean;
	contactName?: string;
	lastStatus?: JobLogType;
	lastStatusTime?: Date;
	headlineTotal?: number;
	usesCommercialHeadline?: boolean;
	headlineBalance?: number;
	Task?: Task[];
	purchaseOrders?: { status: PurchaseOrderStatus; businessSupplier: { supplier: { name: string } } }[];
	problemReason: string | null;
	inWarrantyUntil: string | null;
	customStageId: number | null;
	isJobOverdue?: boolean;
	daysInStage?: string;
	flaggedOn?: Date;
	JobCustomStage?: JobCustomStage[];
	hasPaymentData?: boolean;
	hasWonEstimate?: boolean;
	committedTotal?: number;
	totalInvoiced?: number;
	totalPaid?: number;
};

export enum JobSubView {
	OVERVIEW = 'OVERVIEW',
	ALL = 'ALL',
	ALL_OPPORTUNITIES = 'OPPORTUNITIES',
	ALL_WON_JOBS = 'WON_JOBS',
	FLAGGED = 'FLAGGED',
	WARRANTY = 'WARRANTY',
	CLOSED = 'CLOSED',
	READY_TO_MEASURE = 'READY_TO_MEASURE',
	READY_TO_INSTALL = 'READY_TO_INSTALL',
	READY_TO_ORDER = 'READY_TO_ORDER',
	READY_TO_INVOICE = 'READY_TO_INVOICE',
	CREATED = 'CREATED',
	QUOTE_SENT = 'QUOTE_SENT',
	QUOTE_APPROVED = 'QUOTE_APPROVED',
	MEASURE_SCHEDULED = 'MEASURE_SCHEDULED',
	MEASURE_TAKEN = 'MEASURE_TAKEN',
	DEPOSIT_PAID = 'DEPOSIT_PAID',
	ON_ORDER = 'ON_ORDER',
	PO_RECEIVED = 'PO_RECEIVED',
	PO_REJECTED = 'PO_REJECTED',
	PO_CHECKED_IN = 'PO_CHECKED_IN',
	INSTALL_SCHEDULED = 'INSTALL_SCHEDULED',
	INSTALL_DONE = 'INSTALL_DONE',
	INVOICED = 'INVOICED',
	BILLING_APPROACHING = 'BILLING_APPROACHING',
	UNPAID_PO = 'UNPAID_PO',
}

//Front page jobs
export type JobSummary = Job & {
	address?: Address | null;
	customer: Customer;
	jobOwner: User;
};

export type ReferralSourceOptionRef = { id: number; name: string };

export type ReasonOptionRef = { name: string };

export type JobFull = Job & {
	hasInventoryToPick?: boolean;
	lossReasonRef?: ReasonOptionRef | null;
	referralSourceOption?: ReferralSourceOptionRef | null;
	customer:
		| (Customer & {
				billingAddress: Address | null;
				contacts: Contact[] | null;
				category: {
					name: string;
					id: number;
				} | null;
		  })
		| null;
	address: Address | null;
	jobOwner: User | null;
	contacts: Contact[] | null;
	quickbooksClass?: QuickbooksClass | null;
	JobLog: JobLog[];
	business:
		| (Business & {
				address: Address | null;
				addressTemplates: AddressTemplates[] | null;
				quoteSettings: QuoteSettings[] | null;
				jobCategories: JobCategoryForSettings[] | null;
		  })
		| null;
	branch: (Branch & { address: Address | null }) | null;
	usersOnJobs?: (UsersOnJobs & {
		assignedUser: User;
	})[];
	// Helper Properties which will make it easier for the front
	// end to schedule jobs.
	ScheduledJobs?:
		| (ScheduledJob & {
				usersOnScheduledJob: UsersOnScheduledJobs[];
		  })[]
		| null;
	scheduledJob?:
		| (ScheduledJob & {
				assignedUserIds?: number[];
				assignedUsers?: User[];
		  })
		| null;
	// NB: should only include the single active stage
	JobCustomStage?: {
		stageId: number;
		stage: {
			businessCategory: BusinessCategory;
			jobCategoryId: number | null;
			name: string;
		};
	}[];
	KeyDates?: KeyDate[];
	additionalCustomers: (AdditionalCustomers & { customer: CustomerFull })[];
};

export type JobServiceAllOptions = {
	limit?: number;
	cursor?: number;
	includeRelatedData?: boolean;
	includeProfitAndLossData?: boolean;
	/**
	 * Overrides the relations loaded per job. Defaults to `buildAllInclude(...)`, so callers that don't
	 * set it are unaffected. The front page passes a much narrower include — see `buildFrontPageInclude`.
	 */
	include?: Prisma.JobInclude;
};

export type ScheduleModalJob = {
	id: number;
	jobPhase: JobPhase;
	name: string;
	description: string | null;
	category: JobCategories;
	businessCategory: BusinessCategory | null;
	jobCategoryId: number | null;
	createdAt: Date;
	updatedAt: Date | null;
	address: Address | null;
	customer: {
		id: number;
		name: string;
		contacts: {
			id: number;
			name: string;
			email: string | null;
			phone: string | null;
		}[];
	};
	jobOwner: {
		email: string | null;
		phone: string;
		firstName: string;
		lastName: string;
	};
	ScheduledJobs: ScheduledJobForModalCard[];
	_count: {
		ScheduledJobs: number;
		measurements: number;
		installations: number;
		purchaseOrders: number;
	};
};

export type CustomerWithContactInfo = Customer & {
	phone?: string | null;
	email?: string | null;
};

export type JobForSearchEstimate = Estimate & {
	estimateGroups?: (EstimateGroup & { estimateLineItems: EstimateLineItem[] })[];
	quotes?: { type: PDFType | null; isInvoice: boolean | null; invoiceSentAt: Date | null }[];
};

export type JobForSearch = Job & {
	business?: Business | null | undefined;
	measurements?: Measurement[] | undefined;
	address?: Address | null;

	Document?: Document[] | undefined;
	jobOwner?: User | undefined;
	customer?: (Customer & { contacts?: Contact[]; billingAddress?: Address }) | undefined;
	JobLog?: JobLog[] | undefined;
	estimates?: JobForSearchEstimate[] | undefined;
	approvedChangeOrders?: JobForSearchEstimate[] | undefined;
	quotes?: Quote[] | undefined;
	JobCustomStage?:
		| { stageId: number; isActive: boolean; updatedAt: Date; createdAt: Date; stage?: { daysInStageUntilWarning: number | null } }[]
		| undefined;
	QuickbooksLogs?: QuickbooksLog[] | undefined;
	customerInstallationContact?: Contact | null | undefined;
	purchaseOrders?: PurchaseOrder[] | undefined;
	installations?: Installation[] | undefined;
	ScheduledJobs?:
		| (ScheduledJob & { usersOnScheduledJob?: (UsersOnScheduledJobs & { assignedUser?: User; additionalUsers?: User[] })[] })[]
		| undefined;
	Transactions?: Transaction[] | undefined;
	bidDueDate?: Date;
	businessCategory: BusinessCategory;
	problemReason: string | null;
	promiseDate?: Date;
	inWarrantyUntil?: string | undefined;
	_count?: Prisma.JobCountOutputType | undefined;
};

export type JobThin = Job & {
	address?: Address | null;
	// Hand-written mirror of getIncludeForReporting: `allForReporting`'s findMany sits behind a @ts-ignore, so
	// a relation missing here surfaces at the report formatter rather than at the query.
	lossReasonRef?: ReasonOptionRef | null;
	referralSourceOption?: ReferralSourceOptionRef | null;
	jobOwner?: User | null;
	usersOnJobs?: (UsersOnJobs & { assignedUser: User })[] | null;
	quickbooksClass: {
		quickbooksName: string;
	} | null;
	ScheduledJobs?:
		| {
				id: number;
				start: Date;
				end: Date;
				jobType: string;
		  }[]
		| null;
	customer?: (Customer & { contacts?: Contact[]; billingAddress?: Address; category?: { id: number; name: string } }) | undefined;
	contacts?: Contact[] | null;
	estimates?: Estimate[] | null;
	branch?: Branch | null;
	JobCustomStage?: { stageId: number; stage: CustomStage }[] | null;
	purchaseOrders?: PurchaseOrder[] | null;
	tags?: { tagId: number; tag: { name: string; id: number } }[] | null;
};

export type JobForStats = Job & {
	jobOwner?: User | null;
	usersOnJobs?: (UsersOnJobs & { assignedUser: User })[] | null;
	estimates?: (Estimate & { estimateGroups?: (EstimateGroup & { estimateLineItems: EstimateLineItem[] })[] })[] | null;
	purchaseOrders?: PurchaseOrder[] | null;
};

export type JobForDashboard = Job & {
	estimates?: Estimate[];
	estimateTotal?: number;
	estimateBalance?: number;
};

export type JobResultOfGlobalSearch = {
	id: number;
	internalJobId?: string | null;
	businessCategory?: BusinessCategory;
	jobCategoryId?: number | null;
	customer: {
		name: string;
		isCommercial: boolean;
	};
	name: string;
	jobPhase?: JobPhase;
	jobOwner?: {
		firstName: string;
		lastName: string;
	};
	totalQuoted: number;
	totalWon: number;
	createdAt: Date;
	inWarrantyUntil?: string | null | undefined;
	warrantyDurationDays?: number | null | undefined;
	warrantyStartDate?: Date | null | undefined;
};

export type GlobalSearchCategoryGroup = {
	// The category's row id as a string, the legacy enum for null-pointer jobs, or the
	// QUICK_ESTIMATES pseudo-group. Clients resolve labels and icons through the
	// UserContext category lookups, which accept all three.
	category: string;
	jobs: JobResultOfGlobalSearch[];
	totalInCategory: number;
};

export type JobBoardThin = {
	id: number;
	businessId: number;
	jobPhase: JobPhase;
	name: string;
	category: JobCategories;
	description: string | null;
	deadline: Date | null;
	lastStatus: JobLogType | null;
	lastStatusTime: Date | null;
	jobOwner: { id: number; firstName: string; lastName: string };
	customer: {
		name: string;
	};
};

export type JobForDashboardModal = {
	businessCategory: BusinessCategory | null;
	category: JobCategories;
	customer: {
		name: string;
		id: number;
	};
	customerId: true;
	id: number;
	name: string;
	jobOwner: string;
	estimatesTotal: string;
};

// Option A
// Now we need to bucket the jobs accordingly.
export interface JobBucketResponse {
	[key: string]: JobBoardThin[];
	QuoteSent2DaysAgo: JobBoardThin[]; // includes quotes sent between 2 days and 1 week ago (that are not marked as lost or closed)
	QuoteSent1WeekAgo: JobBoardThin[]; // includes quotes sent between 1 week and 1 month ago (..)
	QuoteSent1MonthAgo: JobBoardThin[]; // includes quotes sent more than 1 month ago (..)
	WonJobsNoDeposit: JobBoardThin[]; // added this one
	WonJobsNoMeasurement: JobBoardThin[]; // replaces WonJobsNoMeasurementsTakenBalance
	WonJobsNoPurchaseOrder: JobBoardThin[];
	MeasurementsTakenWasLastAction: JobBoardThin[];
	PurchaseOrdersNeedConfirmation: JobBoardThin[];
	WaitingForPurchaseOrders: JobBoardThin[];
	AllPurchaseOrdersArrived: JobBoardThin[]; // (Was Last Action ie. nothing else happened)
	FabricationCompleteWasLastAction: JobBoardThin[]; // added this one
	InstallCompleteWasLastAction: JobBoardThin[]; // replaces InstallComplete
	WonJobsOutstandingBalance: JobBoardThin[];
}

export type KeyDateForStepper = Pick<KeyDate, 'completedAt' | 'dueDate' | 'name'> & {
	id?: number;
	assignee?: User | null;
	requiredBeforeStage?: { name: string } | null;
};

/**
 * Job log types to surface in summaries, use in searches, etc
 */
export const ConsequentialJobLogTypes = new Array<JobLogType>(
	JobLogType.JOB_LOST,
	JobLogType.JOB_COMPLETE,
	JobLogType.UNDO_JOB_COMPLETE,
	JobLogType.UNDO_JOB_LOST,
	JobLogType.WORK_ORDER_CREATED,
	JobLogType.JOB_SCHEDULED,
	JobLogType.ESTIMATE_CREATED,
	JobLogType.ESTIMATE_GENERATED,
	JobLogType.ESTIMATE_SENT,
	JobLogType.ESTIMATE_WON,
	JobLogType.UNDO_ESTIMATE_WON,
	JobLogType.ESTIMATE_REJECTED,
	JobLogType.UNDO_ESTIMATE_REJECTED,
	JobLogType.CHANGE_ORDER_REJECTED,
	JobLogType.UNDO_CHANGE_ORDER_REJECTED,
	JobLogType.DEPOSIT_PAID,
	JobLogType.PO_CREATED,
	JobLogType.PO_SENT,
	JobLogType.PO_CONFIRMED,
	JobLogType.PO_REJECTED,
	JobLogType.PO_ACCEPTED,
	JobLogType.PO_PARTIALLY_ACCEPTED,
	JobLogType.PO_FINALIZED,
	JobLogType.PO_PAID,
	JobLogType.FINAL_INVOICE_SENT,
	JobLogType.FINAL_INVOICE_PAID,
	JobLogType.ACCOUNTING_COMPLETE,
	JobLogType.FABRICATION_COMPLETE,
	JobLogType.MEASUREMENT_CREATED,
	JobLogType.INSTALLATION_COMPLETE,
	JobLogType.SERVICE_COMPLETE,
	JobLogType.JOB_CREATED,
	JobLogType.QUOTE_SENT,
	JobLogType.QUOTE_APPROVED,
	JobLogType.QUOTE_SIGNED,
	JobLogType.PROPOSAL_SENT,
	JobLogType.PROPOSAL_SIGNED
);

export class JobSchema {
	static addressInput = z.object({
		id: z.number().nullable().optional(),
		street1: z.string(),
		street2: z.string().optional(),
		city: z.string(),
		state: z.string().optional(),
		province: z.string().optional(),
		zip: z.string(),
		country: z.string().optional(),
		latitude: hiddenInputNumber,
		longitude: hiddenInputNumber,
	});

	static updateJobAddressSchema = z.object({
		jobAddressId: hiddenInputNumber,
		jobAddressStreet1: z.string().optional(),
		jobAddressStreet2: z.string().optional(),
		jobAddressCity: z.string().optional(),
		jobAddressState: z.string().optional(),
		jobAddressZip: z.string().optional(),
		jobAddressLongitude: hiddenInputNumber,
		jobAddressLatitude: hiddenInputNumber,
	});

	static jobsFiltersSchema = DashboardInputSchema.extend({
		phases: z.array(z.string()).optional(),
		assignedUserIds: z.array(z.string()).optional(),
		// The last log type for this job is one of these types
		lastLogTypes: z.array(z.enum(JobLogType)).optional(),
		// At least one of the following log types exist for this job
		anyLogTypes: z.array(z.enum(JobLogType)).optional(),
		// All purchase orders are one of these states (only use one purchase order filter at a time)
		purchaseOrderStates: z.array(z.enum(PurchaseOrderStatus)).optional(),
		// At least one purchase order is in one of these states (only use one purchase order filter at a time)
		somePurchaseOrderStates: z.array(z.enum(PurchaseOrderStatus)).optional(),
		// At least one purchase order is not one of these states (only use one purchase order filter at a time)
		notAllPurchaseOrderStates: z.array(z.enum(PurchaseOrderStatus)).optional(),
		// At least one future scheduled job is one of these types
		futureScheduledJobTypes: z.array(z.enum(ScheduledJobType)).optional(),
		// No future scheduled jobs are one of these types
		disallowedScheduledJobTypes: z.array(z.enum(ScheduledJobType)).optional(),
		// At least one estimate is of one of these states
		estimateStates: z.array(z.enum(EstimateState)).optional(),
		// At least one estimate has a remaining balance
		estimateRequiresBalance: z.boolean().optional(),
		// The monthly billing date for the job is within 7 days
		requireBillingApproaching: z.boolean().optional(),
		// The job must have a problem reason
		requireProblemReason: z.boolean().optional(),
		isWarrantyJob: z.boolean().optional(),
		stageId: z.number().optional(),
	}).optional();

	static allJobsQueryInputSchema = z
		.object({
			filters: JobSchema.jobsFiltersSchema,
			sortBy: z.string().optional(),
			sortDirection: z.string().optional(),
			searchQuery: z.string().trim().optional(),
		})
		.optional();

	static boardViewQueryInputSchema = z.object({
		limit: z.number(),
		cursor: z.number().optional(),
		jobLogType: z.enum(JobLogType),
		filters: DashboardInputSchema.extend({
			assignedUserIds: z.array(z.string()).optional(),
			lastLogTypes: z.array(z.enum(JobLogType)).optional(),
			phases: z.array(z.string()).optional(),
		}).optional(),
		searchQuery: z.string().trim().optional(),
	});

	static frontPageQueryInputSchema = z.object({
		limit: z.number(),
		cursor: z.number().optional(),
		filters: JobSchema.jobsFiltersSchema,
		sortBy: z.string().optional(),
		sortDirection: z.string().optional(),
		searchQuery: z.string().trim().optional(),
	});

	static frontPageCountQueryInputSchema = z.object({
		limit: z.number(),
		cursor: z.number().optional(),
		filtersByJobSubView: z.object({
			[JobSubView.ALL]: JobSchema.jobsFiltersSchema,
			[JobSubView.ALL_OPPORTUNITIES]: JobSchema.jobsFiltersSchema,
			[JobSubView.ALL_WON_JOBS]: JobSchema.jobsFiltersSchema,
			[JobSubView.FLAGGED]: JobSchema.jobsFiltersSchema,
		}),
		searchQuery: z.string().trim().optional(),
	});

	static contact = z.object({
		contactId: z.number().optional(),
		contactName: z.string(),
		contactPhone: z.string().optional(),
		contactEmail: z.string().optional(),
		contactLabel: z.string().optional(),
		isDefaultContact: z.boolean(),
		includeInJob: z.boolean().optional(),
		isFieldContact: z.boolean().optional(),
	});

	// A customer's email, phone and job-site address are deliberately absent: `Customer` has no such
	// columns (contact details live on `Contact`, and the address is `billingAddress` below), so there was
	// nowhere to put them and no client ever sent them. They sat here unpopulated long enough for the
	// quick-estimate lead path to read them as if they were real, which is why it filed every lead with a
	// blank address. Don't reintroduce a field this schema cannot persist.
	static customerDetails = z.object({
		customerId: z.number().optional(),
		customerName: z.string(),
		customerBusinessName: z.string().optional(),
		billingAddress: this.addressInput.optional(),
		isCommercial: z.boolean(),
	});

	static builderPoNumber = z
		.string()
		.trim()
		.max(100)
		.nullable()
		.transform((val) => val || null)
		.optional();

	static createNewJobSchema = z.object({
		customerDetails: JobSchema.customerDetails,
		contactDetails: z.array(JobSchema.contact),
		isPickup: z.boolean(),
		property: z
			.object({
				id: z.number().optional(),
			})
			.optional()
			.nullable(),
		units: z.array(z.number()).optional().nullable(),
		jobDetails: z.object({
			additionalAssignees: z.array(z.object({ text: z.string(), value: z.number() })),
			branchId: z.number().optional(),
			builderPoNumber: JobSchema.builderPoNumber,
			// Optional since jobCategoryId exists: new clients send the row id (the only
			// way to name a custom category), old clients keep sending the enum.
			businessCategory: z.enum(BusinessCategory).optional(),
			jobCategoryId: z.number().int().positive().optional(),
			internalJobId: z.string().trim().min(1).max(100).nullable().optional(),
			isAIAProgressBilling: z.boolean().optional(),
			/// Maintained for mobile app backwards compatibility
			isCommercial: z.boolean().optional(),
			jobAddress: this.addressInput.optional(),
			jobCategory: z.enum(JobCategories),
			jobDeadline: z.string().optional(),
			jobDescription: z.string().optional(),
			jobLead: z.string().optional(),
			jobName: z.string(),
			jobOwnerId: z.number(),
			jobPhase: z.enum(JobPhase),
			jobStatus: z.string().optional(),
			bidDueDate: z.date().optional(),
			promiseDate: z.date().optional(),
			quickbooksClassId: z.number().optional(),
			// Enum kept for the mobile app until it ships the option picker; web sends the option id.
			referralSource: z.enum(ReferralSource).nullish(),
			referralSourceOptionId: z.number().nullish(),
		}),
		quickEstimateId: z.number().optional(),
		leadId: z.number().optional(),
	});

	static createNewMultiCustomerJobSchema = z.object({
		customerIds: z.array(z.number()),
		isPickup: z.boolean(),
		jobDetails: z.object({
			additionalAssignees: z.array(z.object({ text: z.string(), value: z.number() })),
			branchId: z.number().optional(),
			builderPoNumber: JobSchema.builderPoNumber,
			businessCategory: z.enum(BusinessCategory).optional(),
			jobCategoryId: z.number().int().positive().optional(),
			internalJobId: z.string().trim().min(1).max(100).nullable().optional(),
			isAIAProgressBilling: z.boolean(),
			jobAddress: this.addressInput.optional(),
			jobCategory: z.enum(JobCategories),
			jobDeadline: z.string().optional(),
			jobDescription: z.string().optional(),
			jobLead: z.string().optional(),
			jobName: z.string(),
			jobOwnerId: z.number(),
			jobPhase: z.enum(JobPhase),
			jobStatus: z.string().optional(),
			bidDueDate: z.date().optional(),
			promiseDate: z.date().optional(),
			quickbooksClassId: z.number().optional(),
			// Enum kept for the mobile app until it ships the option picker; web sends the option id.
			referralSource: z.enum(ReferralSource).nullish(),
			referralSourceOptionId: z.number().nullish(),
		}),
		jobContactIds: z.array(z.number()),
	});

	static editSchema = z.object({
		aiaBillingTerms: z.enum(AIABillingTerms).optional(),
		approvedBy: z.string().optional(),
		architect: z.string().optional(),
		authorizedAmount: z.number().optional(),
		bidDueDate: z.date().optional().nullable(),
		billingDayOfMonth: z.number().optional(),
		branchId: z.number().optional(),
		builderPoNumber: JobSchema.builderPoNumber,
		businessCategory: z.enum(BusinessCategory).optional(),
		jobCategoryId: z.number().int().positive().optional(),
		category: z.enum(JobCategories).optional(),
		claimNumber: z
			.string()
			.trim()
			.nullable()
			.transform((val) => val || null)
			.optional(),
		commercialBillingToggle: z.boolean().optional(),
		contractDate: z.date().optional(),
		customerId: z.number().optional(),
		dateOfLoss: z
			.union([z.string().transform((val) => (val ? new Date(val) : null)), z.date()])
			.optional()
			.nullable(),
		deductible: z.number().optional().nullable(),
		description: z.string().optional(),
		estimatedBillingPeriods: z.number().int().positive().optional().nullable(),
		estimatedStartDate: z.union([z.string().transform((val) => new Date(val)), z.date()]).optional(),
		flaggedOn: z.null().optional(),
		internalJobId: z.string().trim().min(1).max(100).nullable().optional(),
		isAIAProgressBilling: z.boolean().optional(),
		isPickup: z.boolean().optional(),
		isTaxExempt: z.boolean().optional(),
		jobOwnerId: z.number().optional(),
		jobPhase: z.enum(JobPhase).optional(),
		jobSuperintendent: z.string().optional(),
		lead: z.string().optional(),
		lossReason: z.string().optional(),
		lossReasonCode: z.enum(LossReasonCodes).optional(),
		name: z.string().optional(),
		notes: z.string().optional(),
		policyholder: z
			.string()
			.trim()
			.nullable()
			.transform((val) => val || null)
			.optional(),
		problemReason: z.string().optional(),
		projectNumber: z.string().optional(),
		promiseDate: z.date().optional().nullable(),
		quickbooksClassId: z.number().optional(),
		quickbooksProjectId: z.number().optional().nullable(),
		readyToInstall: z.boolean().optional(),
		readyToInvoice: z.boolean().optional(),
		readyToMeasure: z.boolean().optional(),
		readyToOrder: z.boolean().optional(),
		referralSourceOptionId: z.number().optional().nullable(),
		retainageCostCodeId: z.number().optional().nullable(),
		retainagePercent: z.number().optional(),
		schedulingNote: z.string().optional(),
		useQuickbooksProjects: z.boolean().optional(),
		vehicleVin: z
			.string()
			.trim()
			.toUpperCase()
			.nullable()
			.refine((val) => !val || /^[A-HJ-NPR-Z0-9]{17}$/.test(val), {
				message: 'Enter a valid 17-character VIN (letters and numbers, excluding I, O, and Q).',
			})
			.transform((val) => val || null)
			.optional(),
		warrantyDurationDays: z.number().optional(),
		warrantyStartDate: z.date().optional().nullable(),
		workOrderNotes: z.string().optional(),
	});

	static defaultJobSelect = Prisma.validator<Prisma.JobSelect>()({
		id: true,
		internalJobId: true,
		builderPoNumber: true,
		business: true,
		businessId: true,
		customer: true,
		customerId: true,
		contacts: true,
		jobOwner: true,
		jobOwnerId: true,
		problemReason: true,
		address: true,
		addressId: true,
		name: true,
		category: true,
		description: true,
		deadline: true,
		createdAt: true,
		updatedAt: true,
		deletedAt: true,
		bidDueDate: true,
		promiseDate: true,
		jobPhase: true,
		lostAt: true,
		completedAt: true,
		notes: true,
		lead: true,
		lossReason: true,
		lossReasonId: true,
		lossReasonCode: true,
		workOrderNotes: true,
		isAIAProgressBilling: true,
		isTaxExempt: true,
		useQuickbooksProjects: true,
		quickbooksProjectId: true,
		architect: true,
		aiaBillingTerms: true,
		billingDayOfMonth: true,
		contractDate: true,
		retainagePercent: true,
		retainageCostCodeId: true,
		lastStatus: true,
		lastStatusTime: true,
		isPickup: true,
		isTemplate: true,
		businessCategory: true,
		claimNumber: true,
		dateOfLoss: true,
		deductible: true,
		policyholder: true,
		vehicleVin: true,
		workflowStatus: true,
		readyToMeasure: true,
		readyToInstall: true,
		readyToOrder: true,
		readyToInvoice: true,
		warrantyDurationDays: true,
		warrantyStartDate: true,
		branch: true,
		branchId: true,
	});

	static jobContextSelect = Prisma.validator<Prisma.JobSelect>()({
		addressId: true,
		aiaBillingTerms: true,
		architect: true,
		billingDayOfMonth: true,
		jobCategoryId: true,
		branch: {
			include: {
				address: true,
			},
		},
		builderPoNumber: true,
		businessCategory: true,
		businessId: true,
		category: true,
		claimNumber: true,
		contacts: true,
		contractDate: true,
		dateOfLoss: true,
		deductible: true,
		duplicatedFromJobId: true,
		estimatedBillingPeriods: true,
		id: true,
		internalJobId: true,
		isAIAProgressBilling: true,
		isPickup: true,
		isWarrantyJob: true,
		jobPhase: true,
		name: true,
		policyholder: true,
		problemReason: true,
		projectNumber: true,
		retainagePercent: true,
		retainageCostCodeId: true,
		vehicleVin: true,
		warrantyDurationDays: true,
		warrantyStartDate: true,
		customer: {
			select: {
				billingAddress: true,
				billingAddressId: true,
				billingDayOfMonth: true,
				billingDayOfWeek: true,
				billingStartDate: true,
				contacts: true,
				defaultPaymentTerms: true,
				depositPercent: true,
				id: true,
				isCommercial: true,
				isOnAccount: true,
				isTaxExempt: true,
				name: true,
				onAccountPaymentFrequency: true,
				optOutOfAllNotifications: true,
				payabliPaymentMethods: {
					where: {
						deletedAt: null,
					},
				},
				tilledCustomerId: true,
				tilledPaymentMethods: {
					where: {
						deletedAt: null,
					},
				},
			},
		},
		estimates: {
			where: {
				deletedAt: null,
			},
			select: {
				id: true,
				estimateType: true,
				estimateState: true,
				version: true,
			},
		},
		customerId: true,
		address: true,
		notes: true,
		business: {
			select: {
				enableInternalJobId: true,
				address: true,
				addressId: true,
				addressTemplates: {
					where: {
						deletedAt: null,
					},
				},
				jobCategories: true,
				emailSignature: true,
				jobsUseQBClass: true,
				name: true,
				// Server-side only, to pick `customerPaymentMethods` in `context.forJob`; the client reads it from UserContext.
				payabliEntrypoint: true,
				phone: true,
				quoteSettings: true,
				tilledAccountId: true,
				timezone: true,
				usesGlazierStudio: true,
				usesPartnerPak: true,
				usesWinBidPro: true,
				website: true,
			},
		},
		flaggedOn: true,
	});

	static duplicateJobOptionsSchema = z.object({
		duplicateAttachments: z.boolean().optional(),
		duplicateEstimates: z.boolean().optional(),
		duplicateNotes: z.boolean().optional(),
		duplicateFloorplans: z.boolean().optional(),
	});
}
